import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Controller, UseFieldArrayReturn, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useErrorHandler } from "../../../hooks";
import { TimflowSteadyForm } from "../../../types/forms/capture";
import { CaptureResponse } from "../../../types/model/capture";
import { MethodsInput } from "../../../types/model/methodsInput";
import { CreateInputWithFlow, getFlow, hasValidFlow, SaveInputFlow } from "../../../utils/Methods/Timflow";
import NumberField from "./Numberfield";

interface FlowCellProps {
  captureId: number;
  input?: MethodsInput;
  noInputs: boolean;
  canEdit: boolean;
  onSaveFlow: SaveInputFlow;
  onCreate: CreateInputWithFlow;
  onCreated: (inputId: number) => void;
}

function FlowCell({
  captureId,
  input,
  noInputs,
  canEdit,
  onSaveFlow,
  onCreate,
  onCreated,
}: Readonly<FlowCellProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();

  const current = getFlow(input);
  const initial = hasValidFlow(current) ? String(current) : "";
  const [draft, setDraft] = useState(initial);
  const [isSaving, setIsSaving] = useState(false);

  if (!input && !noInputs) {
    return (
      <Typography color="text.disabled" sx={{ pt: 1 }}>
        —
      </Typography>
    );
  }

  const value = Number(draft);
  const valid = draft !== "" && Number.isFinite(value) && value > 0;
  const changed = draft !== initial;
  const missing = !!input && !hasValidFlow(current);

  const save = async () => {
    try {
      setIsSaving(true);

      if (input) {
        await onSaveFlow(captureId, input.id, value);
      } else {
        const created = await onCreate(captureId, value);
        onCreated(created.id);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  let helperText: string | undefined;
  if (missing) helperText = t("calculations.methods.timflow.flow-missing");
  else if (!input) helperText = t("calculations.methods.timflow.quick-sheet-helper");

  return (
    <Stack direction="row" spacing={0.5} alignItems="flex-start">
      <TextField
        size="small"
        type="number"
        fullWidth
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={!canEdit || isSaving}
        error={missing}
        helperText={helperText}
        slotProps={{
          htmlInput: { step: "any", min: 0 },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                {t("generic.units.m3-per-day")}
              </InputAdornment>
            ),
          },
        }}
      />
      {canEdit && (
        <Tooltip
          title={
            input
              ? t("generic.save")
              : t("calculations.methods.timflow.create-sheet")
          }
        >
          <span>
            <IconButton
              size="small"
              color="success"
              onClick={save}
              disabled={!valid || !changed || isSaving}
              sx={{ mt: 0.5 }}
            >
              {isSaving ? (
                <CircularProgress size={16} />
              ) : input ? (
                <SaveIcon fontSize="small" />
              ) : (
                <AddIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
}

interface WellsTableProps {
  canEdit: boolean;
  captures: CaptureResponse[];
  availableCaptures: CaptureResponse[];
  methodsInputsByCapture: Record<number, MethodsInput[]>;
  isLoadingInputs: boolean;
  wellsArr: UseFieldArrayReturn<TimflowSteadyForm, "wells">;
  onSaveInputFlow: SaveInputFlow;
  onCreateInputWithFlow: CreateInputWithFlow;
}

export default function WellsTable({
  canEdit,
  captures,
  availableCaptures,
  methodsInputsByCapture,
  isLoadingInputs,
  wellsArr,
  onSaveInputFlow,
  onCreateInputWithFlow,
}: Readonly<WellsTableProps>) {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext<TimflowSteadyForm>();
  const wellsValues = watch("wells");
  const tf = (k: string) => t(`calculations.methods.timflow.fields.${k}`);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Toolbar sx={{ gap: 2, flexWrap: "wrap", py: 1.5 }}>
        <Typography variant="h6" sx={{ flex: "1 1 auto" }}>
          {t("calculations.methods.timflow.wells")}
        </Typography>

        {canEdit && (
          <Autocomplete
            size="small"
            sx={{ width: 300 }}
            options={availableCaptures}
            getOptionLabel={(c) => c.name ?? String(c.id)}
            value={null}
            blurOnSelect
            noOptionsText={t("generic.no-records")}
            onChange={(_e, c) =>
              c && wellsArr.append({ captureId: c.id, inputId: "" })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("calculations.methods.timflow.add-capture")}
              />
            )}
          />
        )}
      </Toolbar>

      <TableContainer>
        <Table size="small" sx={{ minWidth: 1000, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "action.hover" }}>
              <TableCell sx={{ width: "12%", fontWeight: 600 }}>
                {tf("capture")}
              </TableCell>
              <TableCell sx={{ width: "22%", fontWeight: 600 }}>
                {tf("input")}
              </TableCell>
              <TableCell sx={{ width: "26%", fontWeight: 600 }}>
                {t("calculations.methods-input.flow-m3-day")}
              </TableCell>
              <TableCell sx={{ width: "20%", fontWeight: 600 }}>
                {tf("rw")}
              </TableCell>
              <TableCell sx={{ width: "20%", fontWeight: 600 }}>
                {tf("res")}
              </TableCell>
              {canEdit && (
                <TableCell sx={{ width: "10%", fontWeight: 600 }} align="right">
                  {t("generic.actions")}
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {wellsArr.fields.map((f, i) => {
              const capture = captures.find((c) => c.id === f.captureId);
              const inputs = methodsInputsByCapture[f.captureId] ?? [];
              const noInputs = !isLoadingInputs && inputs.length === 0;
              const selectedInput = inputs.find(
                (mi) => mi.id === wellsValues[i]?.inputId,
              );

              return (
                <TableRow
                  key={f.id}
                  hover
                  sx={{ "& td": { py: 1.5, verticalAlign: "top" } }}
                >
                  <TableCell>
                    <Chip
                      icon={<WaterDropIcon />}
                      label={capture?.name ?? `#${f.captureId}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{ maxWidth: "100%" }}
                    />
                  </TableCell>

                  <TableCell>
                    <Controller
                      control={control}
                      name={`wells.${i}.inputId`}
                      rules={{
                        required: t("calculations.methods.errors.required"),
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          select
                          required
                          size="small"
                          label={tf("input")}
                          disabled={!canEdit || isLoadingInputs || noInputs}
                          fullWidth
                          error={!!fieldState.error || noInputs}
                          helperText={
                            fieldState.error?.message ??
                            (noInputs
                              ? t("calculations.methods.timflow.no-inputs")
                              : undefined)
                          }
                          {...field}
                        >
                          {inputs.map((mi) => (
                            <MenuItem key={mi.id} value={mi.id}>
                              {mi.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </TableCell>

                  <TableCell>
                    <FlowCell
                      key={`${f.captureId}-${selectedInput?.id ?? "none"}`}
                      captureId={f.captureId}
                      input={selectedInput}
                      noInputs={noInputs}
                      canEdit={canEdit}
                      onSaveFlow={onSaveInputFlow}
                      onCreate={onCreateInputWithFlow}
                      onCreated={(id) => setValue(`wells.${i}.inputId`, id)}
                    />
                  </TableCell>

                  <TableCell>
                    <NumberField
                      name={`wells.${i}.rw`}
                      label=""
                      size="small"
                      disabled={!canEdit}
                    />
                  </TableCell>

                  <TableCell>
                    <NumberField
                      name={`wells.${i}.res`}
                      label=""
                      size="small"
                      disabled={!canEdit}
                    />
                  </TableCell>

                  {canEdit && (
                    <TableCell align="right">
                      {wellsArr.fields.length > 1 && (
                        <Tooltip title={t("generic.delete")}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => wellsArr.remove(i)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}